import { defineStore } from 'pinia';
import { mapAllPlansResponseToPlans, type Plan } from '~/models/Plan.model';
import type { GetAllPlansResponse } from '~/repository/modules/plan.module';

type State = {
  plans: Plan[];
};

export type PlanIdToProductNameMap = { [key: number]: string };

export const usePlanStore = defineStore({
  id: 'plansStore',
  state: (): State => ({
    plans: [],
  }),
  actions: {
    processAndSetPlans(plansResponse: GetAllPlansResponse): void {
      const processedPlans = mapAllPlansResponseToPlans(plansResponse);
      this.plans = processedPlans;
    },
    selectPlanProductName(planId?: number, planShortName?: string): string {
      const fallbackName = planShortName || '';

      if (planId) {
        return this.planIdToProductNameMap[planId] || fallbackName;
      }

      return fallbackName;
    },
  },
  getters: {
    familyParentPlans(state: State): Plan[] {
      return state.plans.filter(
        (plan) => !plan.isFamilyPlan && plan.isFamilyAllowed,
      );
    },
    familyPlans(state: State): Plan[] {
      return state.plans.filter((plan) => plan.isFamilyPlan);
    },
    planIdToProductNameMap(state: State): PlanIdToProductNameMap {
      const newMap: PlanIdToProductNameMap = {};

      for (const plan of state.plans) {
        if (!newMap[plan.planId]) {
          newMap[plan.planId] = plan.productName;
        }
      }

      return newMap;
    },
    studentPlanIds(state: State): number[] {
      const allBundlePrices = state.plans.flatMap((plan) => plan.bundlePrices);
      const allStudentPlanBundlePrices = allBundlePrices.filter(
        (bundlePrice) => bundlePrice.Plan?.IsStudent === true,
      );

      const uniquePlanIdsSet = new Set(
        allStudentPlanBundlePrices.map(
          (bundlePrice) => bundlePrice.Plan?.PlanId,
        ),
      );

      const uniquePlanIdsArray = Array.from(uniquePlanIdsSet);
      const uniquePlanIds = uniquePlanIdsArray.filter(
        (planId) => planId !== undefined,
      );

      return uniquePlanIds;
    },
  },
});
